import { FC, useEffect, useRef, useState } from "react";
import "./ChatsTutorial.scss";
import logo from "../../assets/img/bevi-ajuda-blue.svg";
import { useAuth } from "../../providers/AuthProvider";
import { BvIcon } from "bevi-icon";
import MainNavbar from "../../components/MainNavbar/MainNavbar";
import { ChatsContext } from "../../contexts/ChatsContext";
import ChatMessageList from "../../components/Chats/ChatMessageList/ChatMessageList";
import ChatInput from "../../components/Chats/ChatInput/ChatInput";
import { useChatsTutorial } from "../../providers/Chats/ChatsTutorialProvider";
import { useChats } from "../../providers/Chats/ChatsProvider";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../../providers/Access/Users/UsersProvider";
import ChatSidebar from "../../components/Chats/ChatSidebar/ChatSidebar";
import { ChatAssistant } from "../../models/Chat/ChatAssistantModel";
import { ChatsService } from "../../services/Chats/ChatsService";
import { chatFeatures } from "../../config/chatFeatures";

const SIDEBAR_COLLAPSED_W = 56;
const SIDEBAR_EXPANDED_W = 280;

interface ChatsTutorialProps {}

const ChatsTutorial: FC<ChatsTutorialProps> = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [assistants, setAssistants] = useState<ChatAssistant[]>([]);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { setState } = useChats();
  const { userLogged } = useAuth();
  const {
    tutorial,
    messages,
    stepTutorial,
    agentFinalText,
    isStreaming,
    resetTutorial,
    isVoting,
    isFinished,
  } = useChatsTutorial();
  const { handleFinishedTutorial } = useUsers();

  const hasSidebar = assistants.length > 0;
  const sidebarWidth = hasSidebar
    ? sidebarCollapsed
      ? SIDEBAR_COLLAPSED_W
      : SIDEBAR_EXPANDED_W
    : 0;

  useEffect(() => {
    !isVoting && bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [messages, agentFinalText, isStreaming, isVoting]);

  useEffect(() => {
    setState("tutorial");
  }, []);

  useEffect(() => {
    if (stepTutorial > 0) {
      setState("started");
    }
  }, [tutorial, stepTutorial]);

  useEffect(() => {
    const load = async () => {
      const [assistantsResponse] = await new ChatsService().getAssistants();
      const apiList: ChatAssistant[] | undefined = assistantsResponse?.data;

      if (apiList?.length) {
        setAssistants(apiList);
      } else {
        const fallback: ChatAssistant[] = chatFeatures
          .filter(f => userLogged && f.permissionCheck(userLogged))
          .map((f, i) => ({ id: i + 1, name: f.label, description: '', icon: f.icon, order: i + 1 }));
        setAssistants(fallback);
      }
    };

    load();
  }, [userLogged]);

  useEffect(() => {
    if (isFinished) {
      setIsLoading(true);
      setTimeout(async () => {
        await handleFinishedTutorial();
        resetTutorial();
        navigate("/chats/reply");
      }, 2000);
    }
  }, [isFinished]);

  useEffect(() => {
    document.documentElement.style.setProperty("--chat-sidebar-width", `${sidebarWidth}px`);
    document.documentElement.style.setProperty("--chat-sidebar-gap", hasSidebar ? "12px" : "0px");
    return () => {
      document.documentElement.style.removeProperty("--chat-sidebar-width");
      document.documentElement.style.removeProperty("--chat-sidebar-gap");
    };
  }, [sidebarWidth, hasSidebar]);

  const handleSidebarChange = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  };

  const handleAssistantSelect = () => {
  };

  return (
    <>
      {isLoading && (
        <div className="tutorial-loading-screen">
          <div className="tutorial-loading-content">
            <img src={logo} alt="Logo" className="tutorial-loading-logo" />
            <p className="tutorial-loading-text">Carregando...</p>
          </div>
        </div>
      )}
      <MainNavbar />

      <ChatsContext.Provider>
        <div
          className="chat-page"
          style={{
            paddingTop: "var(--navbar-height)",
            "--background-image": `url(${process.env.PUBLIC_URL}/assets/img/background-wrapper.png)`,
          } as React.CSSProperties}
          data-testid="ChatsTutorial"
        >
          <div className="chat-layout-body">
            {hasSidebar && (
              <ChatSidebar
                assistants={assistants}
                activeAssistantId={null}
                onAssistantSelect={handleAssistantSelect}
                onCollapsedChange={handleSidebarChange}
                isDisabled={true}
              />
            )}

            <div
              className="Chats flex flex-column"
              data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}
            >
              <div className="chat-agent-header">
                <div className="chat-agent-header__row">
                  <BvIcon name="importContacts" width={22.4} height={22.4} color="#222343" />
                  <span className="chat-agent-header__label">Tutorial</span>
                </div>
              </div>
              <div className="chat-messages-container flex-bgs">
                <div className="messages-wrapper">
                  <ChatMessageList
                    messages={messages}
                    isStreaming={isStreaming}
                  />
                </div>
                <div ref={bottomRef} />
              </div>

              <ChatInput isTutorial={true} />
            </div>
          </div>
        </div>
      </ChatsContext.Provider>
    </>
  );
};

export default ChatsTutorial;
